import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  PlayArrow,
  Pause,
  CalendarToday,
  LocationOn,
  Link as LinkIcon,
  EventNote,
  ConfirmationNumber,
  LocalOffer,
} from '@mui/icons-material';
import { useAuth } from "../../../utils/authContext";
import Discounts from "./Discounts";
import EventDetails from "./EventDetails";
import TicketManager from "./TicketManager";
import utils from "../../../utils/utils";
import API from "../../../utils/API";

let globalShowID = 0

export default function EventManager(props) {
	const { showrunner } = useAuth();

	const navigate = useNavigate();

	const initEvent = {
		production: {}
	}

	const [showID, setShowID] = useState(0)
	const [event, setEvent] = useState(initEvent)
	const [show, setShow] = useState({})
	const [prod, setProd] = useState({})
	const [canPublish, setCanPublish] = useState(false)
	const [bankingIsSetUp, setBankingIsSetUp] = useState(false)
	const [showDetails, setShowDetails] = useState(false)
	const [categoryMinimum, setCategoryMinimum] = useState(false)
	const [ticketMinimum, setTicketMinimum] = useState(false)
	const [publishModalOpen, togglePublishModal] = useState(false)
	const [pauseModalOpen, togglePauseModal] = useState(false)
	const [activeTab, setActiveTab] = useState(0)

	const location = useLocation();

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const showID = params.get("showID");
		setShowID(showID);
		globalShowID = showID
		if (!showrunner) return
		const showrunnerID = showrunner.id
		resetEvent(showID, showrunnerID)

		// Show alert message if present in the state
		if (location.state && location.state.alertMessage) {
			utils.sendAlert(location.state.alertMessage, location.state.alertType);
			// Clear the state to ensure the alert only shows once
			navigate({
				...location,
				state: {}
			});
		}
		console.log("location", location)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, showrunner]);

	useEffect(() => {
		if (!showrunner) return
		if (bankingIsSetUp && showDetails && categoryMinimum && ticketMinimum) {
			setCanPublish(true)
		}
	}, [bankingIsSetUp, showDetails, categoryMinimum, ticketMinimum, showrunner])

	const tabs = [
		{ 
			name: "Event Details", 
			component: <EventDetails reset={resetEvent} showID={showID} />,
			icon: <EventNote />
		},
		{ 
			name: "Manage Tickets", 
			component: <TicketManager reset={resetEvent} showrunner={showrunner} />,
			icon: <ConfirmationNumber />
		},
		{ 
			name: "Discount Codes", 
			component: <Discounts showID={globalShowID} />,
			icon: <LocalOffer />
		},
	];

	function checkEventDetails(ev) {
		if (!ev.venue || !ev.location || !ev.date || !ev.production.description || !ev.production.poster_portrait) {
			setShowDetails(false)
		} else {
			setShowDetails(true)

		}
	}

	function resetEvent(id, showRunnerID) {
		API.getShow(id)
			.then(res => {
				setEvent(res.data)
				checkEventDetails(res.data)
			})
		if (showRunnerID) {
			API.getShowManage(id, showRunnerID)
				.then(res => {
					const { can_publish_checklist } = res.data
					const { banking_set_up, category_minimum, ticket_minimum } = can_publish_checklist
					console.log("banking_set_up", banking_set_up)
					setBankingIsSetUp(banking_set_up)
					setCategoryMinimum(category_minimum)
					setTicketMinimum(ticket_minimum)
					checkEventDetails(res.data)
				});
		}
	}

	function updateShow(field, value) {
		if (!canPublish) return
		const updatedShow = { ...event, [field]: value }
		setShow(updatedShow)
		API.updateShow(showID, updatedShow)
			.then(res => {
				utils.sendAlert("Show Updated", "success");
				resetEvent(showID, showrunner.id);
				togglePublishModal(false);
				togglePauseModal(false);
			})
	}
	function redirect(tab) {
    const params = new URLSearchParams(location.search);
    params.set("tab", tab);
         navigate({
          pathname: location.pathname,  // Keep the current pathname
          search: params.toString(),    // Set the updated search params
    });
		togglePublishModal(false);
}
	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	return (
		<Box sx={{ p: 3 }}>
			{/* Event Header */}
			<Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
				<Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
					{event.production?.poster_portrait && (
						<CardMedia
							component="img"
							sx={{ 
								width: { xs: '100%', md: 280 },
								height: { xs: 200, md: 280 },
								objectFit: 'cover',
								borderRadius: { xs: '8px 8px 0 0', md: '8px 0 0 8px' }
							}}
							image={event.production.poster_portrait}
							alt="Event poster"
						/>
					)}
					<CardContent sx={{ flex: 1, p: 4 }}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
							<Box>
								<Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: 'white' }}>
									{event.production?.name || 'Untitled Event'}
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
									<CalendarToday sx={{ color: 'rgba(255,255,255,0.8)' }} />
									<Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
										{utils.formatDateTime(event.date)[0]} at {utils.formatDateTime(event.date)[1]}
									</Typography>
								</Box>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
									<LocationOn sx={{ color: 'rgba(255,255,255,0.8)' }} />
									<Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
										{event.venue} • {event.location}
									</Typography>
								</Box>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
									<LinkIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />
									<Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
										Event URL: 
										<Button
											component="a"
											href={`/event/${showID}`}
											target="_blank"
											rel="noreferrer"
											size="small"
											sx={{ 
												ml: 1, 
												textTransform: 'none',
												color: 'white',
												textDecoration: 'underline'
											}}
										>
											{`https://slick-tix.org/event/${showID}`}
										</Button>
									</Typography>
								</Box>
							</Box>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
								{event.published ? (
									<>
										<Chip 
											label="Published" 
											color="success" 
											variant="filled"
											sx={{ 
												backgroundColor: 'rgba(76, 175, 80, 0.9)',
												color: 'white',
												fontWeight: 'bold'
											}}
										/>
										<Button
											variant="outlined"
											color="warning"
											startIcon={<Pause />}
											onClick={() => togglePauseModal(true)}
											sx={{ 
												borderColor: 'rgba(255,255,255,0.5)',
												color: 'white',
												'&:hover': {
													borderColor: 'white',
													backgroundColor: 'rgba(255,255,255,0.1)'
												}
											}}
										>
											Pause Event
										</Button>
									</>
								) : (
									<>
										<Chip 
											label="Draft" 
											variant="filled"
											sx={{ 
												backgroundColor: 'rgba(255,255,255,0.2)',
												color: 'white',
												fontWeight: 'bold'
											}}
										/>
										<Button
											variant="contained"
											color="success"
											startIcon={<PlayArrow />}
											onClick={() => togglePublishModal(true)}
											sx={{ 
												backgroundColor: 'rgba(76, 175, 80, 0.9)',
												'&:hover': {
													backgroundColor: 'rgba(76, 175, 80, 1)'
												}
											}}
										>
											Go Live
										</Button>
									</>
								)}
							</Box>
						</Box>
					</CardContent>
				</Box>
			</Card>

			{/* Tabs */}
			<Paper sx={{ mb: 3 }}>
				<Tabs 
					value={activeTab} 
					onChange={handleTabChange}
					variant="fullWidth"
					sx={{
						'& .MuiTab-root': {
							minHeight: 60,
							fontSize: '16px',
							fontWeight: 600,
							textTransform: 'none'
						}
					}}
				>
					{tabs.map((tab, index) => (
						<Tab 
							key={tab.name}
							label={tab.name}
							icon={tab.icon}
							iconPosition="start"
						/>
					))}
				</Tabs>
			</Paper>

			{/* Tab Content */}
			<Card>
				<CardContent sx={{ p: 0 }}>
					{tabs[activeTab]?.component}
				</CardContent>
			</Card>

			{/* Publish Modal */}
			<Dialog 
				open={publishModalOpen} 
				onClose={() => togglePublishModal(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					Publish Event
				</DialogTitle>
				<DialogContent>
					{canPublish ? (
						<Alert severity="info" sx={{ mb: 2 }}>
							Are you sure you want to publish this event? This will make it available for purchase by customers.
						</Alert>
					) : (
						<>
							<Typography variant="body1" sx={{ mb: 2 }}>
									You must complete the following before you can publish this show:
							</Typography>
							<List>
								<ListItem 
									button 
									component={Link} 
									to="/showrunner/home"
									sx={{ borderRadius: 1, mb: 1 }}
								>
									<ListItemIcon>
										{bankingIsSetUp ? (
											<CheckCircle color="success" />
										) : (
											<Cancel color="error" />
										)}
									</ListItemIcon>
									<ListItemText 
										primary="Set up your banking details"
										secondary={bankingIsSetUp ? "Complete" : "Required"}
									/>
								</ListItem>
								<ListItem 
									button 
									onClick={() => redirect("Manage+Tickets")}
									sx={{ borderRadius: 1, mb: 1 }}
								>
									<ListItemIcon>
										{categoryMinimum ? (
											<CheckCircle color="success" />
										) : (
											<Cancel color="error" />
										)}
									</ListItemIcon>
									<ListItemText 
										primary="Set at least one ticket category"
										secondary={categoryMinimum ? "Complete" : "Required"}
									/>
								</ListItem>
								<ListItem 
									button 
									onClick={() => redirect("Manage+Tickets")}
									sx={{ borderRadius: 1, mb: 1 }}
								>
									<ListItemIcon>
										{ticketMinimum ? (
											<CheckCircle color="success" />
										) : (
											<Cancel color="error" />
										)}
									</ListItemIcon>
									<ListItemText 
										primary="Set at least one ticket"
										secondary={ticketMinimum ? "Complete" : "Required"}
									/>
								</ListItem>
								<ListItem 
									button 
									onClick={() => redirect("Event+Details")}
									sx={{ borderRadius: 1 }}
								>
									<ListItemIcon>
										{showDetails ? (
											<CheckCircle color="success" />
										) : (
											<Cancel color="error" />
										)}
									</ListItemIcon>
									<ListItemText 
										primary="Set show details"
										secondary={showDetails ? "Complete" : "Required"}
									/>
								</ListItem>
							</List>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => togglePublishModal(false)}>
						Cancel
					</Button>
					<Button 
						onClick={() => updateShow("published", true)} 
						disabled={!canPublish}
						variant="contained"
						color="success"
					>
						Publish
					</Button>
				</DialogActions>
			</Dialog>

			{/* Pause Modal */}
			<Dialog 
				open={pauseModalOpen} 
				onClose={() => togglePauseModal(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					Pause Event
				</DialogTitle>
				<DialogContent>
					<Alert severity="warning" sx={{ mb: 2 }}>
						By clicking the button below, your event will no longer be accessible to customers. 
						You can republish at any time until the event date has passed.
					</Alert>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => togglePauseModal(false)}>
						Cancel
					</Button>
					<Button 
						onClick={() => updateShow("published", false)} 
						variant="contained"
						color="warning"
					>
						Pause Event
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	)
}